<script setup lang="ts">
import { notificationService } from '@/services/notification/notification-service';
import {
  PhDownloadSimple,
  PhFilePlus,
  PhFiles,
  PhPencilSimple,
} from '@phosphor-icons/vue';
import { onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import VuePdfEmbed from 'vue-pdf-embed';
import { AtaDetailsCard } from '../ata-details-card';
import { PdfPreviewModal } from '../pdf-preview-modal';
import PdfMergerModal, { type StagedItem } from './PdfMergerModal.vue';
import {
  appendPdfFiles,
  DEFAULT_MAX_FILES,
  isExternalFileDrag,
  mergePdfs,
  type PdfBatchRejectReason,
} from './usePdfMerge';

const props = withDefaults(
  defineProps<{
    title?: string;
    icon?: Component;
    fileName?: string;
    downloadFileName?: string;
    initialDocument?: Blob | null;
    isLoading?: boolean;
    readOnly?: boolean;
    maxBytes?: number;
  }>(),
  {
    title: undefined,
    icon: () => PhFiles,
    fileName: 'combined.pdf',
    downloadFileName: undefined,
    initialDocument: null,
    isLoading: false,
    readOnly: false,
    maxBytes: 30 * 1024 * 1024,
  },
);

const emit = defineEmits<{
  'document-change': [file: File | null];
}>();

const { t } = useI18n();

const isModalOpen = ref(false);
const isPreviewOpen = ref(false);

const stagedItems = ref<StagedItem[]>([]);
const merged = ref<{ file: File; pageCount: number } | null>(null);

const hasPendingUserChange = ref(false);

const hydrateFromInitialDocument = (blob: Blob | null) => {
  if (hasPendingUserChange.value) return;
  if (!blob) {
    merged.value = null;
    stagedItems.value = [];
    return;
  }
  const file = new File([blob], props.fileName, { type: 'application/pdf' });
  merged.value = { file, pageCount: 0 };
  stagedItems.value = [{ id: 'initial-document', file }];
};

onMounted(() => hydrateFromInitialDocument(props.initialDocument));
watch(
  () => props.initialDocument,
  (blob) => {
    hasPendingUserChange.value = false;
    hydrateFromInitialDocument(blob);
  },
);

const previewUrl = ref<string | null>(null);
watch(
  () => merged.value?.file,
  (file) => {
    if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = file ? URL.createObjectURL(file) : null;
  },
);

const openModal = () => {
  if (props.readOnly) return;
  isModalOpen.value = true;
};

const openPreview = () => {
  if (!merged.value) return;
  isPreviewOpen.value = true;
};

const downloadMerged = () => {
  if (!merged.value || !previewUrl.value) return;
  const anchor = document.createElement('a');
  anchor.href = previewUrl.value;

  anchor.download = props.downloadFileName ?? props.fileName;
  anchor.click();
};

const onModalSave = (payload: {
  blob: Blob | null;
  pageCount: number;
  items: StagedItem[];
}) => {
  stagedItems.value = payload.items;
  hasPendingUserChange.value = true;

  if (!payload.blob) {
    merged.value = null;
    isModalOpen.value = false;
    emit('document-change', null);
    return;
  }

  const file = new File([payload.blob], props.fileName, {
    type: 'application/pdf',
  });
  merged.value = { file, pageCount: payload.pageCount };
  isModalOpen.value = false;
  emit('document-change', file);
};

const isDraggingField = ref(false);
const isDraggingViewer = ref(false);

const DRAG_IDLE_MS = 150;
let fieldDragTimer: ReturnType<typeof setTimeout> | undefined;
let viewerDragTimer: ReturnType<typeof setTimeout> | undefined;

const onFieldDragOver = (event: DragEvent) => {
  if (!isExternalFileDrag(event)) return;
  isDraggingField.value = true;
  clearTimeout(fieldDragTimer);
  fieldDragTimer = setTimeout(() => {
    isDraggingField.value = false;
  }, DRAG_IDLE_MS);
};

const onViewerDragOver = (event: DragEvent) => {
  if (props.readOnly || !isExternalFileDrag(event)) return;
  isDraggingViewer.value = true;
  clearTimeout(viewerDragTimer);
  viewerDragTimer = setTimeout(() => {
    isDraggingViewer.value = false;
  }, DRAG_IDLE_MS);
};

const notifyRejection = (reason: PdfBatchRejectReason) => {
  const text = (() => {
    switch (reason) {
      case 'notPdf':
        return t('pdfMerger.notifications.notPdf');
      case 'tooManyFiles':
        return t('pdfMerger.notifications.tooManyFiles', {
          count: DEFAULT_MAX_FILES,
        });
      case 'tooLarge':
        return t('pdfMerger.notifications.tooLarge', {
          maxMb: Math.round(props.maxBytes / (1024 * 1024)),
        });
    }
  })();
  notificationService.open({ text, type: 'warning' });
};

const onDrop = async (event: DragEvent) => {
  event.preventDefault();
  clearTimeout(fieldDragTimer);
  clearTimeout(viewerDragTimer);
  isDraggingField.value = false;
  isDraggingViewer.value = false;
  if (props.readOnly || props.isLoading) return;

  const dataTransferFiles = event.dataTransfer?.files;
  if (!dataTransferFiles?.length) return;

  const priorItems = stagedItems.value;
  const priorIds = new Set(priorItems.map((item) => item.id));

  const result = appendPdfFiles(priorItems, dataTransferFiles, {
    maxBytes: props.maxBytes,
  });
  result.rejectionReasons.forEach(notifyRejection);

  if (result.addedCount === 0) return;

  const mergeResult = await mergePdfs(result.items);

  const newIds = result.items
    .filter((item) => !priorIds.has(item.id))
    .map((item) => item.id);
  const badIds = new Set(
    mergeResult.errored
      .filter((entry) => newIds.includes(entry.id))
      .map((entry) => entry.id),
  );
  if (badIds.size > 0) {
    notificationService.open({
      text: t('pdfMerger.notifications.unreadable'),
      type: 'warning',
    });
  }

  const finalItems = result.items.filter((item) => !badIds.has(item.id));
  if (finalItems.length === priorItems.length) return;

  const file = new File([mergeResult.blob!], props.fileName, {
    type: 'application/pdf',
  });
  merged.value = { file, pageCount: mergeResult.pageCount };
  stagedItems.value = finalItems;
  hasPendingUserChange.value = true;
  emit('document-change', file);
};

onBeforeUnmount(() => {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
});
</script>

<template>
  <AtaDetailsCard
    class="pdf-merger"
    :icon="icon"
    :title="title ?? t('ata.details.cards.ataDocuments')"
  >
    <template v-if="merged" #header-actions>
      <div class="pdf-merger__header-actions">
        <button
          type="button"
          class="button is-small is-rounded"
          @click="downloadMerged"
        >
          <PhDownloadSimple :size="16" class="mr-1" />
          {{ t('pdfMerger.download') }}
        </button>
        <button
          v-if="!readOnly"
          type="button"
          class="button is-small is-rounded"
          @click="openModal"
        >
          <PhPencilSimple :size="16" class="mr-1" />
          {{ t('pdfMerger.reopen') }}
        </button>
      </div>
    </template>

    <div v-if="isLoading" class="pdf-merger__spinner">
      <span class="pdf-merger__spinner-circle" />
      <span class="is-size-7 mt-2">{{ t('pdfMerger.loadingDocument') }}</span>
    </div>

    <div
      v-else-if="merged && previewUrl"
      class="pdf-merger__viewer"
      :class="{ 'pdf-merger__viewer--dragging': isDraggingViewer }"
      role="button"
      tabindex="0"
      :aria-label="t('pdfMerger.openPreview')"
      @click="openPreview"
      @keydown.enter="openPreview"
      @keydown.space.prevent="openPreview"
      @dragover.prevent="onViewerDragOver"
      @drop="onDrop"
    >
      <VuePdfEmbed :source="previewUrl" class="pdf-merger__doc" />
    </div>

    <button
      v-else-if="!readOnly"
      type="button"
      class="pdf-merger__field"
      :class="{ 'pdf-merger__field--dragging': isDraggingField }"
      @click="openModal"
      @dragover.prevent="onFieldDragOver"
      @drop="onDrop"
    >
      <PhFilePlus :size="24" class="mr-2" />
      {{ t('pdfMerger.addCombinedDocument') }}
    </button>

    <PdfMergerModal
      v-if="isModalOpen"
      :initial-items="stagedItems"
      :max-bytes="maxBytes"
      @save="onModalSave"
      @close="isModalOpen = false"
    />

    <PdfPreviewModal
      v-if="isPreviewOpen && merged"
      :file="merged.file"
      @close="isPreviewOpen = false"
    />
  </AtaDetailsCard>
</template>

<style scoped>
.pdf-merger :deep(.card-content) {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.pdf-merger__header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem;
}

.pdf-merger__field {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  width: 100%;
  min-height: 12rem;
  padding: 1.25rem;
  border: 2px dashed #b5b5b5;
  border-radius: 8px;
  background: #fff;
  color: #4a4a4a;
  cursor: pointer;
}

.pdf-merger__field:hover,
.pdf-merger__field--dragging {
  border-color: #3273dc;
  color: #3273dc;
}

.pdf-merger__field--dragging {
  background: rgba(50, 115, 220, 0.06);
}

.pdf-merger__spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  width: 100%;
  min-height: 12rem;
  color: #7a7a7a;
}

.pdf-merger__spinner-circle {
  position: relative;
  width: 3rem;
  height: 3rem;
}

.pdf-merger__spinner-circle:before {
  content: '';
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border-top: 0.3rem solid var(--primary-color);
  border-right: 0.3rem solid transparent;
  animation: pdf-merger-spin 0.6s linear infinite;
}

@keyframes pdf-merger-spin {
  to {
    transform: rotate(360deg);
  }
}

.pdf-merger__viewer {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  border: 2px solid transparent;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.pdf-merger__viewer--dragging {
  border-color: #3273dc;
  background-color: rgba(50, 115, 220, 0.06);
}

.pdf-merger__doc {
  width: 100%;
  max-width: 100%;
}

.pdf-merger__doc :deep(canvas),
.pdf-merger__doc :deep(svg),
.pdf-merger__doc :deep(img) {
  max-width: 100%;
  height: auto;
}
</style>
